import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, User, CheckCircle2, AlertCircle, ArrowLeft, Mail, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";

function RequestInviteForm() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/staff/request-invite", { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="text-center py-4" data-testid="request-invite-success">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center fb-status-success">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Check your inbox</h2>
        <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>
          If <strong>{email}</strong> matches an invited staff account, a new setup link is on its way.
          The link will be valid for 24 hours.
        </p>
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium underline"
          style={{ color: "var(--fb-text-muted)" }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Request a new link</h1>
      <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>
        Enter the email address your invite was sent to and we'll send a fresh setup link.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Email address</label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
            <input
              data-testid="request-invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              placeholder="you@example.com"
              className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
              style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 fb-status-error text-sm p-3 rounded-lg" data-testid="request-invite-error">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          data-testid="request-invite-submit"
          type="submit"
          disabled={loading}
          className="w-full fb-btn-primary py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
          {loading ? "Sending…" : "Send me a new link"}
        </button>
      </form>

      <div className="mt-6 text-center text-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 font-medium underline"
          style={{ color: "var(--fb-text-muted)" }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
      </div>
    </>
  );
}

export default function StaffSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithData } = useAuth();
  const token = searchParams.get("token") || "";

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);

  const isExpiredError = error.toLowerCase().includes("expired");

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your full name."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/staff/setup-password", { token, name: name.trim(), password });
      await loginWithData(data);
      setDone(true);
      setTimeout(() => navigate("/staff"), 1500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  if (!token || showRequestForm) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
        <div className="max-w-md w-full fb-card p-6 sm:p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Logo size={44} />
            <div>
              <div className="font-bold" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
              <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Staff Portal</div>
            </div>
          </div>
          <RequestInviteForm />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--fb-bg)" }}>
      <div className="max-w-md w-full fb-card p-6 sm:p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={44} />
          <div>
            <div className="font-bold" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
            <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Staff Portal</div>
          </div>
        </div>

        {!done ? (
          <>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Set up your account</h1>
            <p className="text-sm mb-6" style={{ color: "var(--fb-text-muted)" }}>
              Enter your name and choose a password to activate your staff account.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full name</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
                  <input
                    data-testid="setup-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    placeholder="Your full name"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
                  <input
                    data-testid="setup-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm password</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
                  <input
                    data-testid="setup-confirm-input"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your password"
                    className="w-full pl-10 pr-3 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] transition-all"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                </div>
              </div>

              {error && (
                <div className="fb-status-error text-sm p-3 rounded-lg" data-testid="setup-error">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                  {isExpiredError && (
                    <button
                      type="button"
                      onClick={() => setShowRequestForm(true)}
                      className="mt-2 text-xs font-semibold underline underline-offset-2 block"
                      data-testid="request-new-link-btn"
                    >
                      Request a new setup link →
                    </button>
                  )}
                </div>
              )}

              <button
                data-testid="setup-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full fb-btn-primary py-3 rounded-lg font-semibold"
              >
                {loading ? "Activating…" : "Activate my account"}
              </button>
            </form>

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="inline-flex items-center gap-1.5 font-medium underline"
                style={{ color: "var(--fb-text-muted)" }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="setup-success">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center fb-status-success">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Account activated!</h2>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Your account is ready. Taking you to the dashboard…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
